import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import {
  UploadIcon,
  Scan,
  ChevronLeft,
  Pill,
  CheckCircle2,
  Loader,
  AlertTriangle,
  Building2,
  Stethoscope,
  Calendar,
  FlaskConical,
} from "lucide-react";
import BottomNavigation from "../components/bottom_navigation";

export default function PrescriptionPage({ addToCart }) {
  const navigate = useNavigate();
  const [medicines, setMedicines] = useState([]);
  const [prescriptionResult, setPrescriptionResult] = useState(null); // { status, analysis, requiresPharmacistReview, imageUrl? }
  const [savedPrescriptions, setSavedPrescriptions] = useState([]);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [loading, setLoading] = useState(false);

  // Save-as-name flow
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState("");

  const videoRef = useRef(null);

  // Saved prescriptions are already individual documents (each with its own
  // analysis), so we just sort them by most recently issued/created.
  const sortedSavedPrescriptions = useMemo(() => {
    return [...(savedPrescriptions || [])].sort((a, b) => {
      const aDate = new Date(a?.issuedDate || a?.createdAt || 0);
      const bDate = new Date(b?.issuedDate || b?.createdAt || 0);
      return bDate - aDate;
    });
  }, [savedPrescriptions]);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      setSavedPrescriptions([]);
      return;
    }
    loadSavedPrescriptions(userId);
  }, []);

  const loadSavedPrescriptions = async (userId) => {
    try {
      const res = await api.get(`/api/prescription/patient/${userId}`);
      const list = Array.isArray(res?.data?.prescriptions) ? res.data.prescriptions : [];
      setSavedPrescriptions(list);
    } catch (err) {
      console.error("Failed to load saved prescriptions", err);
      setSavedPrescriptions([]);
    }
  };

  // Upload
  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    await processPrescription(file);
  };

  // Scan
  const handleScan = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      videoRef.current.srcObject = stream;
      videoRef.current.play();

      setTimeout(async () => {
        const canvas = document.createElement("canvas");
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg"));
        await processPrescription(blob);

        stream.getTracks().forEach((track) => track.stop());
      }, 2000);
    } catch (err) {
      console.error(err);
    }
  };

  // Send image to backend OCR / analysis
  const processPrescription = async (fileOrBlob) => {
    setLoading(true);
    setPrescriptionResult(null);
    setMedicines([]);
    setSaveName("");
    setSaveSuccess(false);
    setSaveError("");

    try {
      const formData = new FormData();
      formData.append("prescription", fileOrBlob);

      const res = await api.post("/api/prescriptions/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      handleProcessedResult(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessedResult = (data) => {
    if (data?.status === "processed" && data?.analysis) {
      setPrescriptionResult(data);
      setMedicines(Array.isArray(data.analysis.medicines) ? data.analysis.medicines : []);
    } else {
      setPrescriptionResult(null);
      setMedicines([]);
    }
  };

  const handleSavePrescription = async () => {
    if (!saveName.trim()) {
      setSaveError("Please give this prescription a name");
      return;
    }
    if (!prescriptionResult) return;

    setSaving(true);
    setSaveError("");
    try {
      const userId = localStorage.getItem("userId");
      const res = await api.post("/api/prescription/save", {
        owner : userId,
        title: saveName.trim(),
        analysis: prescriptionResult.analysis,
        requiresPharmacistReview: prescriptionResult.requiresPharmacistReview,
        imageUrl: prescriptionResult.imageUrl || null,
        source: "uploaded",
      });

      // Optimistically add the newly saved doc to the list if returned,
      // otherwise just refetch.
      const savedDoc = res?.data?.prescription;
      if (savedDoc) {
        setSavedPrescriptions((prev) => [savedDoc, ...prev]);
      } else if (userId) {
        await loadSavedPrescriptions(userId);
      }

      setSaveSuccess(true);
    } catch (err) {
      console.error("Failed to save prescription", err);
      setSaveError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f8f6fc", paddingBottom: 90 }}>
      {/* Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #eceaf3", padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", cursor: "pointer", display: "grid", placeItems: "center" }}>
          <ChevronLeft size={24} color="#1a1a2e" />
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#1a1a2e", letterSpacing: "-0.3px" }}>Upload Prescription</h1>
          <p style={{ margin: "2px 0 0", fontSize: 12.5, color: "#6b6781" }}>Get medicine recommendations</p>
        </div>
      </div>

      <div style={{ padding: "20px 16px", maxWidth: 600, margin: "0 auto" }}>
        {/* Hero Section */}
        <div style={{ background: "linear-gradient(135deg, #6b31ff 0%, #4f21de 100%)", borderRadius: 20, padding: 24, color: "#fff", marginBottom: 20 }}>
          <Pill size={32} style={{ marginBottom: 12, opacity: 0.9 }} />
          <h2 style={{ fontSize: 26, fontWeight: 800, margin: "0 0 8px", lineHeight: 1.1 }}>
            Upload Your Prescription
          </h2>
          <p style={{ fontSize: 14, margin: 0, opacity: 0.95, lineHeight: 1.4 }}>
            We'll analyze it and suggest medicines available in our pharmacy
          </p>
        </div>

        {/* Upload & Scan Cards */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 }}>
          <label
            style={{
              background: "#fff", border: "1px solid #eceaf3", borderRadius: 16, padding: 20,
              cursor: "pointer", textAlign: "center", transition: "all 0.2s ease",
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              gap: 10, minHeight: 140,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#fafaf8";
              e.currentTarget.style.borderColor = "#6b31ff";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(107, 49, 255, 0.12)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#fff";
              e.currentTarget.style.borderColor = "#eceaf3";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#f3edff", display: "grid", placeItems: "center" }}>
              <UploadIcon size={24} color="#6b31ff" />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#1a1a2e", marginBottom: 2 }}>Upload</div>
              <div style={{ fontSize: 12, color: "#6b6781" }}>From device</div>
            </div>
            <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleUpload} disabled={loading} />
          </label>

          <div
            onClick={handleScan}
            style={{
              background: "#fff", border: "1px solid #eceaf3", borderRadius: 16, padding: 20,
              cursor: "pointer", textAlign: "center", transition: "all 0.2s ease",
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              gap: 10, minHeight: 140,
              opacity: loading ? 0.6 : 1,
              pointerEvents: loading ? "none" : "auto",
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.background = "#fafaf8";
                e.currentTarget.style.borderColor = "#6b31ff";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(107, 49, 255, 0.12)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#fff";
              e.currentTarget.style.borderColor = "#eceaf3";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#f3edff", display: "grid", placeItems: "center" }}>
              <Scan size={24} color="#6b31ff" />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#1a1a2e", marginBottom: 2 }}>Scan</div>
              <div style={{ fontSize: 12, color: "#6b6781" }}>With camera</div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{ background: "#fff", borderRadius: 16, padding: 24, textAlign: "center" }}>
            <Loader size={32} style={{ margin: "0 auto 12px", color: "#6b31ff", animation: "spin 1s linear infinite" }} />
            <p style={{ fontSize: 15, fontWeight: 600, color: "#1a1a2e", marginBottom: 4 }}>Processing prescription</p>
            <p style={{ fontSize: 13, color: "#6b6781" }}>Please wait while we analyze your prescription...</p>
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Full Prescription Result (just analyzed) */}
        {prescriptionResult && !loading && (
          <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #eceaf3", overflow: "hidden", marginBottom: 16 }}>
            <div style={{ padding: 18, borderBottom: "1px solid #eceaf3", background: "#f8f6fc" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <CheckCircle2 size={20} color="#059669" />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#1a1a2e" }}>Prescription Analyzed</div>
                  <div style={{ fontSize: 12, color: "#6b6781", marginTop: 2 }}>
                    {medicines.length} medicine{medicines.length !== 1 ? "s" : ""} detected
                  </div>
                </div>
              </div>
            </div>

            <div style={{ padding: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
                <MetaBox icon={<Building2 size={14} color="#6b31ff" />} label="Clinic" value={prescriptionResult.analysis?.clinic} />
                <MetaBox icon={<Stethoscope size={14} color="#6b31ff" />} label="Doctor" value={prescriptionResult.analysis?.doctor} />
                <MetaBox icon={<Calendar size={14} color="#6b31ff" />} label="Date" value={prescriptionResult.analysis?.date} />
                {prescriptionResult.requiresPharmacistReview && (
                  <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 10, padding: "10px 12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                      <AlertTriangle size={14} color="#c2410c" />
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#c2410c", textTransform: "uppercase" }}>Review</span>
                    </div>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: "#9a3412" }}>Pharmacist review required</div>
                  </div>
                )}
              </div>

              {Array.isArray(prescriptionResult.analysis?.warnings) && prescriptionResult.analysis.warnings.length > 0 && (
                <WarningsBox warnings={prescriptionResult.analysis.warnings} />
              )}

              {medicines.length > 0 && (
                <div style={{ marginBottom: 4 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "#6b6781", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.3 }}>
                    Medicines
                  </div>
                  {medicines.map((med, idx) => (
                    <MedicineRow key={med._id || idx} med={med} isLast={idx === medicines.length - 1} onAdd={() => addToCart && addToCart(med)} />
                  ))}
                </div>
              )}

              {/* Save section */}
              <div style={{ borderTop: "1px solid #eceaf3", paddingTop: 14, marginTop: 10 }}>
                {saveSuccess ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 10, padding: "10px 12px" }}>
                    <CheckCircle2 size={16} color="#059669" />
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#065f46" }}>Saved as "{saveName}"</span>
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: "#6b6781", marginBottom: 8 }}>Save this prescription</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <input
                        type="text"
                        value={saveName}
                        onChange={(e) => { setSaveName(e.target.value); setSaveError(""); }}
                        placeholder="e.g. Malaria treatment - Jan 2026"
                        style={{ flex: 1, border: "1px solid #eceaf3", borderRadius: 10, padding: "10px 12px", fontSize: 13, outline: "none" }}
                      />
                      <button
                        onClick={handleSavePrescription}
                        disabled={saving}
                        style={{
                          background: saving ? "#c9b8ff" : "linear-gradient(135deg, #6b31ff, #4f21de)",
                          border: "none", borderRadius: 10, padding: "10px 18px", color: "#fff",
                          fontWeight: 700, fontSize: 13, cursor: saving ? "default" : "pointer", whiteSpace: "nowrap",
                        }}
                      >
                        {saving ? "Saving..." : "Save"}
                      </button>
                    </div>
                    {saveError && <div style={{ fontSize: 12, color: "#dc2626", marginTop: 6 }}>{saveError}</div>}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {!loading && !prescriptionResult && (
          <div style={{ background: "#fff", borderRadius: 16, padding: 20, textAlign: "center", color: "#6b6781" }}>
            <p style={{ fontSize: 14, margin: 0, lineHeight: 1.5 }}>
              Upload or scan a prescription to get started. Our system will extract medicine details and recommend available options.
            </p>
          </div>
        )}

        {/* Saved Prescriptions List */}
        {sortedSavedPrescriptions.length > 0 && (
          <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #eceaf3", marginTop: 16, overflow: "hidden" }}>
            <div style={{ padding: 16, borderBottom: "1px solid #eceaf3", background: "#f8f6fc" }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#1a1a2e" }}>Saved Prescriptions</div>
              <div style={{ fontSize: 12, color: "#6b6781", marginTop: 2 }}>
                {sortedSavedPrescriptions.length} saved
              </div>
            </div>

            <div style={{ padding: 12 }}>
              {sortedSavedPrescriptions.map((rx, index) => (
                <div
                  key={rx._id || index}
                  style={{
                    borderBottom: index < sortedSavedPrescriptions.length - 1 ? "1px solid #f0eefc" : "none",
                    padding: "12px 0",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#1a1a2e" }}>
                      {rx.title || "Prescription"}
                    </div>
                    <StatusBadge status={rx.status} />
                  </div>

                  <div style={{ fontSize: 12, color: "#6b6781", marginTop: 4 }}>
                    {rx.analysis?.doctor ? `${rx.analysis.doctor} · ` : ""}
                    {(rx.analysis?.medicines?.length || 0)} medicine{(rx.analysis?.medicines?.length || 0) !== 1 ? "s" : ""}
                  </div>

                  {rx.analysis?.clinic && (
                    <div style={{ fontSize: 11, color: "#8b87a3", marginTop: 2 }}>{rx.analysis.clinic}</div>
                  )}

                  <div style={{ fontSize: 11, color: "#8b87a3", marginTop: 4 }}>
                    Issued: {new Date(rx.issuedDate || rx.createdAt).toLocaleDateString()}
                  </div>

                  <button
                    onClick={() => setSelectedPrescription(rx)}
                    style={{
                      marginTop: 10, background: "#f3edff", border: "1px solid #e4dbff", borderRadius: 10,
                      padding: "8px 12px", color: "#4f21de", fontWeight: 700, fontSize: 12, cursor: "pointer",
                    }}
                  >
                    View Full Prescription
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <video ref={videoRef} style={{ display: "none" }} />

      {/* Full-detail modal for a saved prescription */}
      {selectedPrescription && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(14, 10, 30, 0.52)", zIndex: 1000,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
          }}
          onClick={() => setSelectedPrescription(null)}
        >
          <div
            style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 520, maxHeight: "85vh", overflowY: "auto", border: "1px solid #eceaf3" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: 16, borderBottom: "1px solid #eceaf3", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 16, color: "#1a1a2e" }}>
                  {selectedPrescription.title || "Prescription"}
                </div>
                <div style={{ fontSize: 12, color: "#6b6781", marginTop: 2 }}>
                  {new Date(selectedPrescription.issuedDate || selectedPrescription.createdAt).toLocaleDateString()}
                </div>
              </div>
              <button
                onClick={() => setSelectedPrescription(null)}
                style={{ border: "1px solid #eceaf3", background: "#fff", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontWeight: 700, color: "#4b5563" }}
              >
                Close
              </button>
            </div>

            <div style={{ padding: 16 }}>
              {selectedPrescription.imageUrl && (
                <img
                  src={selectedPrescription.imageUrl}
                  alt="Prescription"
                  style={{ width: "100%", borderRadius: 12, border: "1px solid #eceaf3", marginBottom: 14, objectFit: "cover", maxHeight: 260 }}
                />
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
                <MetaBox icon={<Building2 size={14} color="#6b31ff" />} label="Clinic" value={selectedPrescription.analysis?.clinic} />
                <MetaBox icon={<Stethoscope size={14} color="#6b31ff" />} label="Doctor" value={selectedPrescription.analysis?.doctor} />
                <MetaBox icon={<Calendar size={14} color="#6b31ff" />} label="Date" value={selectedPrescription.analysis?.date} />
                {selectedPrescription.requiresPharmacistReview && (
                  <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 10, padding: "10px 12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                      <AlertTriangle size={14} color="#c2410c" />
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#c2410c", textTransform: "uppercase" }}>Review</span>
                    </div>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: "#9a3412" }}>Pharmacist review required</div>
                  </div>
                )}
              </div>

              {Array.isArray(selectedPrescription.analysis?.warnings) && selectedPrescription.analysis.warnings.length > 0 && (
                <WarningsBox warnings={selectedPrescription.analysis.warnings} />
              )}

              {Array.isArray(selectedPrescription.analysis?.medicines) && selectedPrescription.analysis.medicines.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "#6b6781", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.3 }}>
                    Medicines
                  </div>
                  {selectedPrescription.analysis.medicines.map((med, idx) => (
                    <div key={med._id || idx} style={{ border: "1px solid #eceaf3", borderRadius: 12, padding: 12, marginBottom: 10 }}>
                      <div style={{ fontWeight: 700, color: "#1a1a2e", marginBottom: 4 }}>{med.name}</div>
                      <div style={{ fontSize: 12.5, color: "#5b5772" }}>
                        {med.strength ? `Strength: ${med.strength}` : "Strength: Not specified"}
                      </div>
                      {med.frequency && <div style={{ fontSize: 12.5, color: "#5b5772" }}>Frequency: {med.frequency}</div>}
                      {med.duration && <div style={{ fontSize: 12.5, color: "#5b5772" }}>Duration: {med.duration}</div>}
                    </div>
                  ))}
                </div>
              )}

              {Array.isArray(selectedPrescription.labTests) && selectedPrescription.labTests.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                    <FlaskConical size={14} color="#6b31ff" />
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: "#6b6781", textTransform: "uppercase", letterSpacing: 0.3 }}>Lab Tests</span>
                  </div>
                  {selectedPrescription.labTests.map((test, idx) => (
                    <div key={idx} style={{ fontSize: 12.5, color: "#334155", marginBottom: 4 }}>
                      • {typeof test === "string" ? test : test?.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <BottomNavigation />
    </div>
  );
}

// --- Small presentational helpers ---

function MetaBox({ icon, label, value }) {
  return (
    <div style={{ background: "#f8f6fc", borderRadius: 10, padding: "10px 12px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
        {icon}
        <span style={{ fontSize: 11, fontWeight: 700, color: "#8b87a3", textTransform: "uppercase" }}>{label}</span>
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e" }}>{value || "Not specified"}</div>
    </div>
  );
}

function WarningsBox({ warnings }) {
  return (
    <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: 12, marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <AlertTriangle size={15} color="#dc2626" />
        <span style={{ fontSize: 12.5, fontWeight: 700, color: "#991b1b" }}>Warnings</span>
      </div>
      {warnings.map((w, i) => (
        <div key={i} style={{ fontSize: 12.5, color: "#991b1b", marginTop: 2 }}>• {w}</div>
      ))}
    </div>
  );
}

function MedicineRow({ med, isLast, onAdd }) {
  return (
    <div style={{ padding: "12px 0", borderBottom: isLast ? "none" : "1px solid #f0eefc" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: "#1a1a2e", marginBottom: 4, wordBreak: "break-word" }}>
            {med.name}
          </div>
          <div style={{ fontSize: 12, color: "#6b6781" }}>
            {med.strength && <>Strength: <span style={{ fontWeight: 600, color: "#1a1a2e" }}>{med.strength}</span></>}
            {med.frequency && <> · Freq: <span style={{ fontWeight: 600, color: "#1a1a2e" }}>{med.frequency}</span></>}
          </div>
        </div>
        <button
          onClick={onAdd}
          style={{
            background: "linear-gradient(135deg, #6b31ff, #4f21de)", border: "none", borderRadius: 10,
            padding: "8px 14px", color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap",
          }}
        >
          Add to Cart
        </button>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { bg: "#fff7ed", color: "#c2410c", label: "Pending" },
    processed: { bg: "#ecfdf5", color: "#059669", label: "Processed" },
    reviewed: { bg: "#eff6ff", color: "#2563eb", label: "Reviewed" },
  };
  const s = map[status] || { bg: "#f1f5f9", color: "#475569", label: status || "Unknown" };
  return (
    <span style={{ background: s.bg, color: s.color, fontSize: 10.5, fontWeight: 700, padding: "3px 8px", borderRadius: 999, whiteSpace: "nowrap" }}>
      {s.label}
    </span>
  );
}